import { Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useStockStatus } from "@/hooks/notifications/useStockStatus";
import { cn } from "@/lib/utils";

interface StockStatusBadgeProps {
  notificationId: string;
  onClick?: () => void;
}

export const StockStatusBadge = ({ notificationId, onClick }: StockStatusBadgeProps) => {
  const { data: stockStatus, isLoading, error } = useStockStatus(notificationId);

  // Log errors for debugging
  if (error) {
    console.error('Error loading stock status:', error);
  }

  if (isLoading || !stockStatus) {
    return null;
  }

  const { totalProducts, productsInStock } = stockStatus;

  // Don't show badge if no products are monitored
  if (totalProducts === 0) {
    return null;
  }

  // Determine variant based on stock status
  let variant: "destructive" | "warning" | "secondary" = "secondary";
  
  if (productsInStock === totalProducts && totalProducts > 0) {
    variant = "destructive"; // All in stock (red - alert!)
  } else if (productsInStock > 0 && productsInStock < totalProducts) {
    variant = "warning"; // Partially in stock (orange)
  } else {
    variant = "secondary"; // None in stock (gray)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <Badge 
      variant={variant} 
      className={cn(
        "gap-1 transition-all duration-300",
        onClick && "cursor-pointer hover:scale-110",
        variant === "destructive" && "animate-pulse shadow-lg shadow-destructive/50 font-semibold"
      )}
      onClick={onClick ? handleClick : undefined}
    >
      <Package className="h-3 w-3" />
      {productsInStock}/{totalProducts} op voorraad
    </Badge>
  );
};
